import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Search, ShoppingBag } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Iter 9.48 - ServiceAddonsModal
 * Host attaches host-owned products (from db.products) to a provider_service
 * so they show up as "Recommended Add-Ons" on the client Confirmation page.
 *
 * Props:
 *   open, onOpenChange
 *   service: provider_service row { service_id, name, addon_product_ids?, addon_notes? }
 *   eventId: parent dashboard event id (used to fetch host's products)
 */
export default function ServiceAddonsModal({ open, onOpenChange, service, eventId }) {
  const [allProducts, setAllProducts] = useState([]);
  const [linkedIds, setLinkedIds] = useState([]);
  const [notes, setNotes] = useState({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !eventId) return;
    // Fetch host's products
    const token = localStorage.getItem("session_token");
    axios
      .get(`${API}/api/host/events/${eventId}/host-brand-products`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setAllProducts(r.data?.products || []))
      .catch(() => setAllProducts([]));
  }, [open, eventId]);

  useEffect(() => {
    if (!open || !service?.service_id) return;
    // Fetch current add-ons for this service
    axios
      .get(`${API}/api/services/${service.service_id}/addons`)
      .then((r) => {
        const ids = (r.data?.addons || []).map((p) => p.product_id);
        const noteMap = {};
        (r.data?.addons || []).forEach((p) => {
          if (p.host_note) noteMap[p.product_id] = p.host_note;
        });
        setLinkedIds(ids);
        setNotes(noteMap);
      })
      .catch(() => {
        setLinkedIds(service.addon_product_ids || []);
        setNotes(service.addon_notes || {});
      });
  }, [open, service]);

  const toggle = (pid) => {
    setLinkedIds((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]
    );
  };

  const save = async () => {
    if (!service?.service_id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      await axios.put(
        `${API}/api/services/${service.service_id}/addons`,
        { addon_product_ids: linkedIds, addon_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onOpenChange(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save add-ons");
    } finally {
      setSaving(false);
    }
  };

  const filtered = allProducts.filter((p) =>
    !search ? true : (p.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] flex flex-col"
        data-testid="service-addons-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-rose-600" />
            Add-Ons for {service?.name || "Service"}
          </DialogTitle>
          <p className="text-xs text-gray-500 mt-1">
            Attach your products as recommended upsells. Clients see them on
            the Confirmation page after they book this service.
          </p>
        </DialogHeader>

        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your products..."
            className="pl-8 h-9 text-sm"
            data-testid="addon-search-input"
          />
        </div>

        <div className="overflow-y-auto flex-1 space-y-1.5">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              No products yet. Add products in Settings → Operations → Products.
            </div>
          ) : (
            filtered.map((p) => {
              const isLinked = linkedIds.includes(p.product_id);
              return (
                <div
                  key={p.product_id}
                  className={`p-2.5 rounded-lg border transition-all ${
                    isLinked
                      ? "border-rose-300 bg-rose-50"
                      : "border-gray-200 hover:border-rose-200"
                  }`}
                  data-testid={`addon-product-${p.product_id}`}
                >
                  <div className="flex items-center gap-2.5">
                    {p.image_url && (
                      <img
                        src={p.image_url}
                        alt=""
                        className="w-10 h-10 rounded object-cover shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        {p.price && (
                          <span className="text-xs text-gray-500">${p.price}</span>
                        )}
                      </div>
                      {p.brand_name && (
                        <span className="text-[10px] text-rose-500">{p.brand_name}</span>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant={isLinked ? "default" : "outline"}
                      onClick={() => toggle(p.product_id)}
                      className="text-xs h-7"
                      data-testid={`toggle-addon-${p.product_id}`}
                    >
                      {isLinked ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    </Button>
                  </div>
                  {isLinked && (
                    <Textarea
                      value={notes[p.product_id] || ""}
                      onChange={(e) =>
                        setNotes({ ...notes, [p.product_id]: e.target.value })
                      }
                      placeholder="Optional note shown under this product (e.g. 'Use this lip oil 24h before our session')"
                      className="mt-2 text-xs min-h-[40px]"
                      data-testid={`addon-note-${p.product_id}`}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-between items-center pt-2 border-t">
          <span className="text-xs text-gray-500">
            {linkedIds.length} attached
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              data-testid="addon-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="addon-save-btn"
            >
              {saving ? "Saving..." : "Save Add-Ons"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
